import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pharmacyStockService, PharmacyStockFilters } from '../pharmacyStockService';
import type { RetentionUnit } from '../../../types/retention';

export function usePharmacyStock(page = 1, limit = 20, filters?: PharmacyStockFilters) {
  return useQuery({
    queryKey: ['pharmacy-stock', page, limit, filters],
    queryFn: () => pharmacyStockService.getStock(page, limit, filters),
    staleTime: 20000,
    placeholderData: (previousData) => previousData,
  });
}

export function usePharmacyBatches(drugId?: string, expiringInDays?: number) {
  return useQuery({
    queryKey: ['pharmacy-batches', drugId, expiringInDays],
    queryFn: () => pharmacyStockService.getBatches(drugId, expiringInDays),
    staleTime: 20000,
  });
}

export function usePharmacyExpiring(days = 30) {
  return useQuery({
    queryKey: ['pharmacy-expiring', days],
    queryFn: () => pharmacyStockService.getExpiring(days),
    staleTime: 30000,
  });
}

export function usePharmacyLedger(
  page = 1,
  limit = 20,
  filters?: { drugId?: string; type?: string; from?: string; to?: string; search?: string },
) {
  return useQuery({
    queryKey: ['pharmacy-ledger', page, limit, filters],
    queryFn: () => pharmacyStockService.getLedger(page, limit, filters),
    staleTime: 20000,
    placeholderData: (previousData) => previousData,
  });
}

/** Invalidate everything a ledger deletion affects (stock is derived from the ledger). */
function invalidateLedgerCaches(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ['pharmacy-ledger'] });
  queryClient.invalidateQueries({ queryKey: ['pharmacy-stock'] });
  queryClient.invalidateQueries({ queryKey: ['pharmacy-batches'] });
}

/** Delete a single ledger entry. Renders its own toast, so the global one is hidden. */
export function useDeletePharmacyLedgerEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: (id: string) => pharmacyStockService.deleteLedgerEntry(id),
    onSuccess: () => invalidateLedgerCaches(queryClient),
  });
}

/** Bulk-delete ledger entries. Renders its own toast, so the global one is hidden. */
export function useBulkDeletePharmacyLedger() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: (ids: string[]) => pharmacyStockService.bulkDeleteLedger(ids),
    onSuccess: () => invalidateLedgerCaches(queryClient),
  });
}

/** Preview how many ledger entries a retention purge would remove. */
export function usePharmacyLedgerPurgePreview(
  value: number,
  unit: RetentionUnit,
  enabled = true,
) {
  return useQuery({
    queryKey: ['pharmacy-ledger', 'purgePreview', value, unit],
    queryFn: () => pharmacyStockService.previewPurgeLedger(value, unit),
    enabled: enabled && value > 0,
    staleTime: 0,
    retry: false,
  });
}

/** Permanently delete ledger entries older than the retention window. */
export function usePurgePharmacyLedger() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: ({ value, unit }: { value: number; unit: RetentionUnit }) =>
      pharmacyStockService.purgeLedger(value, unit),
    onSuccess: () => invalidateLedgerCaches(queryClient),
  });
}

export function useAdjustPharmacyStock() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: pharmacyStockService.adjustStock,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-stock'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-drugs'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-batches'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-ledger'] });
    },
  });
}

export function useSetPharmacyStock() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: pharmacyStockService.setStock,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-stock'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-drugs'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-drug'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-batches'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-ledger'] });
    },
  });
}

export function useIssuePharmacyStock() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: pharmacyStockService.issueStock,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-stock'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-drugs'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-batches'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-ledger'] });
    },
  });
}
